(() => {
  const SHOW_EVENT = 'theme:signal';
  const DISMISS_EVENT = 'theme:signal-dismiss';
  const STATES = new Set(['info', 'accent', 'success', 'warning', 'error', 'loading']);
  const DEFAULT_DURATION = {
    info: 3000,
    accent: 1800,
    success: 2800,
    warning: 4500,
    error: 6000,
    loading: 0,
  };
  const DEFAULT_EMOJI = {
    info: 'ℹ️',
    accent: '✨',
    success: '✅',
    warning: '⚠️',
    error: '❗',
    loading: '',
  };

  class StorefrontSignal extends HTMLElement {
    constructor() {
      super();
      this.currentKey = '';
      this.remaining = 0;
      this.deadline = 0;
      this.timer = undefined;
      this.hideTimer = undefined;
      this.variantChangeUnsubscriber = undefined;
      this.previouslyFocused = undefined;

      this.onShow = this.onShow.bind(this);
      this.onDismissRequest = this.onDismissRequest.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.pauseTimer = this.pauseTimer.bind(this);
      this.resumeTimer = this.resumeTimer.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.surface = this.querySelector('[data-signal-surface]');
      this.icon = this.querySelector('[data-signal-icon]');
      this.titleElement = this.querySelector('[data-signal-title]');
      this.messageElement = this.querySelector('[data-signal-message]');
      this.action = this.querySelector('[data-signal-action]');
      this.dismissButton = this.querySelector('[data-signal-dismiss]');

      if (!this.surface || !this.messageElement) return;

      document.addEventListener(SHOW_EVENT, this.onShow);
      document.addEventListener(DISMISS_EVENT, this.onDismissRequest);
      this.addEventListener('pointerenter', this.pauseTimer);
      this.addEventListener('pointerleave', this.resumeTimer);
      this.addEventListener('focusin', this.pauseTimer);
      this.addEventListener('focusout', this.resumeTimer);
      this.dismissButton?.addEventListener('click', () => this.dismiss({ restoreFocus: true }));

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      this.initialized = true;
    }

    disconnectedCallback() {
      document.removeEventListener(SHOW_EVENT, this.onShow);
      document.removeEventListener(DISMISS_EVENT, this.onDismissRequest);
      this.removeEventListener('pointerenter', this.pauseTimer);
      this.removeEventListener('pointerleave', this.resumeTimer);
      this.removeEventListener('focusin', this.pauseTimer);
      this.removeEventListener('focusout', this.resumeTimer);
      this.variantChangeUnsubscriber?.();
      clearTimeout(this.timer);
      clearTimeout(this.hideTimer);
      this.initialized = false;
    }

    onShow(event) {
      this.show(event.detail);
    }

    onDismissRequest(event) {
      const key = this.cleanText(event.detail?.key, 80);
      if (key && key !== this.currentKey) return;
      this.dismiss();
    }

    onVariantChange({ data } = {}) {
      const variant = data?.variant;
      const optionTitle = this.cleanText(variant?.public_title, 160);
      if (!optionTitle) return;

      const available = Boolean(variant.available);
      const soldOut = this.cleanText(window.variantStrings?.soldOut, 80);

      this.show({
        key: `variant:${data.sectionId || 'product'}`,
        state: available ? 'accent' : 'error',
        emoji: available ? '✨' : '❗',
        message: available
          ? `Selected · ${optionTitle}`
          : `${optionTitle} · ${soldOut || window.variantStrings?.unavailable || 'Unavailable'}`,
        duration: available ? DEFAULT_DURATION.accent : DEFAULT_DURATION.success,
        dismissible: false,
      });
    }

    show(detail = {}) {
      const signal = this.normalize(detail);
      if (!signal) return;

      clearTimeout(this.timer);
      clearTimeout(this.hideTimer);
      if (this.hidden && document.activeElement instanceof HTMLElement && !this.contains(document.activeElement)) {
        this.previouslyFocused = document.activeElement;
      }
      this.currentKey = signal.key;
      this.hidden = false;
      this.surface.dataset.state = signal.state;
      this.surface.setAttribute('role', signal.state === 'error' ? 'alert' : 'status');
      this.surface.setAttribute('aria-live', signal.state === 'error' ? 'assertive' : 'polite');

      this.icon.textContent = signal.state === 'loading' ? '' : signal.emoji;
      this.icon.hidden = signal.state !== 'loading' && !signal.emoji;
      this.titleElement.textContent = signal.title;
      this.titleElement.hidden = !signal.title;
      this.messageElement.textContent = signal.message;
      this.renderAction(signal.action);

      const dismissible = signal.dismissible ?? Boolean(signal.action || signal.duration === 0 || ['warning', 'error'].includes(signal.state));
      this.dismissButton.hidden = !dismissible;

      requestAnimationFrame(() => this.setAttribute('open', ''));
      this.armTimer(signal.duration);
    }

    dismiss({ restoreFocus = false } = {}) {
      if (this.hidden) return;

      clearTimeout(this.timer);
      this.timer = undefined;
      this.remaining = 0;
      this.removeAttribute('open');

      if (restoreFocus && this.previouslyFocused instanceof HTMLElement && this.previouslyFocused.isConnected) {
        this.previouslyFocused.focus({ preventScroll: true });
      }

      this.hideTimer = setTimeout(() => {
        this.hidden = true;
        this.currentKey = '';
        this.messageElement.textContent = '';
      }, this.prefersReducedMotion() ? 0 : 180);
    }

    normalize(detail) {
      const state = STATES.has(detail.state) ? detail.state : 'info';
      const message = this.cleanText(detail.message, 240);
      if (!message) return null;

      const requestedDuration = typeof detail.duration === 'number' ? detail.duration : Number.NaN;
      const duration = detail.persist === true
        ? 0
        : Number.isFinite(requestedDuration)
          ? Math.max(0, Math.min(requestedDuration, 10000))
          : DEFAULT_DURATION[state];

      return {
        key: this.cleanText(detail.key, 80) || 'storefront',
        state,
        title: this.cleanText(detail.title, 80),
        message,
        emoji: detail.emoji === false ? '' : this.cleanText(detail.emoji, 8) || DEFAULT_EMOJI[state],
        duration,
        dismissible: typeof detail.dismissible === 'boolean' ? detail.dismissible : undefined,
        action: this.normalizeAction(detail.action),
      };
    }

    normalizeAction(action) {
      const label = this.cleanText(action?.label, 40);
      const href = this.safeHref(action?.href);
      return label && href ? { label, href } : null;
    }

    renderAction(action) {
      if (!this.action) return;

      this.action.hidden = !action;
      this.action.textContent = action?.label || '';
      if (action) {
        this.action.href = action.href;
      } else {
        this.action.removeAttribute('href');
      }
    }

    safeHref(value) {
      const href = this.cleanText(value, 500);
      if (!href) return '';

      try {
        const url = new URL(href, window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
      } catch {
        return '';
      }
    }

    cleanText(value, maxLength) {
      return typeof value === 'string' ? value.trim().slice(0, maxLength) : '';
    }

    armTimer(duration) {
      this.remaining = duration;
      if (duration === 0) {
        this.timer = undefined;
        return;
      }
      this.deadline = Date.now() + duration;
      this.timer = setTimeout(() => {
        this.timer = undefined;
        this.dismiss();
      }, duration);
    }

    pauseTimer() {
      if (!this.timer) return;
      clearTimeout(this.timer);
      this.timer = undefined;
      this.remaining = Math.max(0, this.deadline - Date.now());
    }

    resumeTimer() {
      queueMicrotask(() => {
        if (this.timer || this.remaining <= 0 || this.matches(':hover') || this.contains(document.activeElement)) return;
        this.deadline = Date.now() + this.remaining;
        this.timer = setTimeout(() => {
          this.timer = undefined;
          this.dismiss();
        }, this.remaining);
      });
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  if (!customElements.get('storefront-signal')) {
    customElements.define('storefront-signal', StorefrontSignal);
  }

  window.StorefrontSignal = Object.freeze({
    show(detail) {
      document.dispatchEvent(new CustomEvent(SHOW_EVENT, { detail }));
    },
    dismiss(key = '') {
      document.dispatchEvent(new CustomEvent(DISMISS_EVENT, { detail: { key } }));
    },
  });
})();
